import { maybe, rd } from '@passionware/monads';
import { get } from 'lodash';
import { Info, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Selection } from 'react-aria-components';
import { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getFormattedEventDates } from 'v1/helpers/byModel/EventHelper';
import { getDimmedContainerClassName } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { useProductionFilters } from 'v5/features/productions/useProductionFilters';
import { Production } from '../../../../__types__';
import {
  productionQueryUtils,
  ProductionSearchQuery
} from '../../../api/production/production.api';
import { Badge } from '../../../design-system/Badge';
import { Button } from '../../../design-system/Button';
import {
  ListBox,
  ListBoxHeader,
  ListBoxItem,
  ListBoxItemContent,
  ListBoxSkeleton
} from '../../../design-system/ListBox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../../../design-system/Popover';
import { EntityListToolbar } from 'v5/patterns/entity-list/EntityListToolbar';
import { cn } from '../../../platform/dom/cn';
import { rowLevelValidation } from '../../../platform/row-level-validation';
import { ScrollShadowWrapper } from '../../../primitives/ScrollShadowWrapper';
import { getProductionIcon } from '../_shared/productionHelpers';
import type { CallsheetFormData } from '../types';
import { myServices } from '../../../services/services.connected.app';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';

type ProductionStatus = {
  id: number;
  name: string;
  status_type: string;
  colour: string;
};

function withDefaultDialogFilters(
  query: ProductionSearchQuery
): ProductionSearchQuery {
  return productionQueryUtils
    .transform(query)
    .build(q => [
      q.withFilter(
        'archived',
        query.filters?.archived ?? { operator: 'equal', value: false },
        { preservePage: true }
      )
    ]);
}

export interface ProductionSelectionStepProps
  extends WithServices<
    [
      WithProductionService,
      WithProductionTypeService,
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithUserPreferencesService,
      WithAppScopeService
    ]
  > {
  form: UseFormReturn<CallsheetFormData>;
}

/**
 * Production Selection Step - First step in callsheet creation
 *
 * Allows users to select one or more productions to auto-populate callsheets.
 * Includes search, filtering, and keyboard navigation support.
 */
export function ProductionSelectionStep({
  form,
  services
}: ProductionSelectionStepProps) {
  const [selectionPopoverOpen, setSelectionPopoverOpen] = useState(false);

  const selectedProductionIds = form.watch('selectedProductionIds');
  const selectedIds = useMemo(
    () => new Set(selectedProductionIds),
    [selectedProductionIds]
  );

  // Get workspace ID
  const app_id = useSelector((state: any) => state?.auth?.app_id);

  // Get statuses from Redux store
  const statuses = useSelector((state: any) => state?.statuses?.data || {});

  const persistedQuery =
    services.userPreferencesService.callsheetsProductionQuery.useValue();
  const query = withDefaultDialogFilters(
    rd.getOrElse(persistedQuery, productionQueryUtils.ofDefault())
  );
  const setQuery = (nextQuery: ProductionSearchQuery) =>
    services.userPreferencesService.callsheetsProductionQuery.setValue(
      withDefaultDialogFilters(nextQuery)
    );

  const filterConfig = useProductionFilters(
    maybe.map(
      rd.tryMap(
        services.appScopeService.useAppScope(),
        appScopeUtils.identityFromResolved
      ),
      scope => ({ type: 'production' as const, scope })
    ),
    services,
    undefined
  );

  // Fetch productions
  const productionsData = rd.useLastWithPlaceholder(
    myServices.productionService.useProductions({
      workspace: app_id,
      ...query
    })
  );

  const productions = rowLevelValidation.validDataFromRows(
    rd.tryGet(productionsData)?.items ?? []
  );

  // Fetch selected productions by IDs
  const selectedProductionIdsArray = Array.from(selectedIds);

  const selectedProductionsRecord =
    myServices.productionService.useProductionsById(
      app_id,
      selectedProductionIdsArray
    );

  // Extract productions from record, filtering out non-success states
  const selectedProductions = useMemo(() => {
    return selectedProductionIdsArray
      .map(id => {
        const productionData = selectedProductionsRecord[id];
        return rd.tryGet(productionData);
      })
      .filter((p): p is Production => p !== undefined);
  }, [selectedProductionsRecord, selectedProductionIdsArray]);

  const removeSelection = (id: number) => {
    const currentIds = form.getValues('selectedProductionIds');
    form.setValue(
      'selectedProductionIds',
      currentIds.filter(pid => pid !== id),
      { shouldDirty: true }
    );
  };

  const clearAllSelections = () => {
    form.setValue('selectedProductionIds', [], {
      shouldDirty: true
    });
  };

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      form.setValue(
        'selectedProductionIds',
        productions.map(p => p.id),
        { shouldDirty: true }
      );
    } else {
      form.setValue('selectedProductionIds', Array.from(keys).map(Number), {
        shouldDirty: true
      });
    }
  };

  const getStatusInfo = (statusId: number): ProductionStatus | null => {
    return get(statuses, [statusId], null);
  };

  const getStatusBadgeVariant = (statusType: string) => {
    switch (statusType) {
      case 'READY':
        return { variant: 'info' as const, label: 'Ready' };
      case 'RESOURCING':
        return { variant: 'attention' as const, label: 'Resourcing' };
      case 'OPEN':
        return { variant: 'gray' as const, label: 'Open' };
      case 'ACTIVE':
        return { variant: 'accent' as const, label: 'Active' };
      case 'COMPLETE':
        return { variant: 'success' as const, label: 'Complete' };
      case 'ALERT':
        return { variant: 'danger' as const, label: 'Alert' };
      default:
        return { variant: 'gray' as const, label: statusType };
    }
  };

  const formatDate = (production: Production) => {
    if (!production.production_date) {
      return 'No date';
    }
    return getFormattedEventDates(production.production_date);
  };

  const getProductionSubtitle = (production: Production) => {
    // Try to extract subtitle from code or other fields
    if (typeof production.code === 'object' && production.code?.nodes) {
      return production.code.nodes.map(n => n.id).join(' - ');
    }
    return production.code as string | undefined;
  };

  return (
    <>
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Select production</h2>
        <p className="text-fg-muted">
          Auto-populate callsheets from one or many productions
        </p>
      </div>

      {/* Search and Filters */}
      {rd
        .journey(filterConfig)
        .wait(null)
        .catch(() => null)
        .map(filterConfig => (
          <EntityListToolbar
            query={query}
            onQueryChange={setQuery}
            searchPlaceholder="Search productions"
            filterConfig={filterConfig.configs}
            groups={filterConfig.groups}
            order={filterConfig.order}
            hiddenFilters={['archived']}
            paginationTotalCount={rd.map(productionsData, data => data.count)}
          />
        ))}

      {/* Productions List */}
      <div className="border border-border-default rounded-lg flex flex-col min-h-0 flex-1">
        {/* Header - not scrollable, always visible */}
        {selectedIds.size > 0 ? (
          <Popover
            open={selectionPopoverOpen}
            onOpenChange={setSelectionPopoverOpen}
          >
            <ListBoxHeader>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-my-1.5 text-base font-medium text-fg-default hover:text-fg-default cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-brand-300 focus-visible:ring-inset rounded px-1 -mx-1"
                  aria-label={`${selectedIds.size} production${selectedIds.size === 1 ? '' : 's'} selected. Click to view details.`}
                >
                  {selectedIds.size} selected
                </Button>
              </PopoverTrigger>
              <Button
                variant="ghost"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  clearAllSelections();
                }}
                className="font-normal underline underline-offset-3 -my-2"
                aria-label="Unselect all productions"
              >
                Unselect All
              </Button>
            </ListBoxHeader>
            <PopoverContent
              className="w-[600px] p-0 max-h-[400px] flex flex-col shadow-lg"
              align="start"
              side="top"
            >
              <div className="px-4 py-3 border-b border-border-default bg-bg-default shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base text-fg-default">
                    Selected Productions
                  </h3>
                  <span className="text-sm text-fg-muted">
                    {selectedIds.size}{' '}
                    {selectedIds.size === 1 ? 'item' : 'items'}
                  </span>
                </div>
              </div>
              <ScrollShadowWrapper className="min-h-0 overflow-y-auto">
                <div
                  className="divide-y divide-border-default"
                  role="list"
                  aria-label="Selected productions"
                >
                  {selectedProductions.map(production => {
                    const status = getStatusInfo(production.status_id);
                    const statusBadge = status
                      ? getStatusBadgeVariant(status.status_type)
                      : null;
                    const subtitle = getProductionSubtitle(production);
                    const color = production.color;

                    return (
                      <div
                        key={production.id}
                        role="listitem"
                        className="group/item relative"
                      >
                        {/* Color bar - positioned absolutely on the left */}
                        {color && (
                          <span
                            className="absolute left-2 inset-y-2 w-[2px]"
                            style={{ backgroundColor: color }}
                            aria-hidden="true"
                          />
                        )}
                        <div className="flex items-center gap-4 p-4 hover:bg-bg-subtle transition-colors">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <ListBoxItemContent
                              icon={getProductionIcon(production)}
                              primary={
                                production.title || (
                                  <span className="text-fg-subtle italic">
                                    (no name)
                                  </span>
                                )
                              }
                              secondary={
                                subtitle || (
                                  <span className="text-fg-subtle italic">
                                    (no code)
                                  </span>
                                )
                              }
                              extra={
                                <div className="flex items-center gap-3 text-sm text-fg-muted">
                                  <span>{formatDate(production)}</span>
                                  {statusBadge && (
                                    <Badge
                                      variant={statusBadge.variant}
                                      tone="subtle"
                                      size="sm"
                                    >
                                      {statusBadge.label}
                                    </Badge>
                                  )}
                                </div>
                              }
                              showCheckmark={false}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSelection(production.id)}
                            className="h-7 w-7 p-0 shrink-0 opacity-0 group-hocus/item:opacity-100 transition-opacity hover:bg-bg-danger hover:text-fg-danger rounded"
                            aria-label={`Remove ${production.title || 'production'} from selection`}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollShadowWrapper>
            </PopoverContent>
          </Popover>
        ) : (
          <ListBoxHeader>
            <div className="text-base text-fg-muted flex items-center gap-2">
              <Info className="size-4 shrink-0" aria-hidden="true" />
              <span>Select one or multiple productions</span>
            </div>
          </ListBoxHeader>
        )}
        {/* Scrollable list */}
        <ScrollShadowWrapper className="flex-1 min-h-0 overflow-y-auto">
          {rd
            .journey(productionsData)
            .wait(
              <div aria-label="Loading productions" role="list">
                <ListBoxSkeleton numRows={4} />
              </div>
            )
            .catch(error => (
              <div className="p-8 text-center text-fg-danger">
                Error loading productions: {String(error)}
              </div>
            ))
            .map(data => (
              <ListBox
                aria-label="Available productions"
                selectionMode="multiple"
                selectedKeys={selectedIds}
                onSelectionChange={handleSelectionChange}
                renderEmptyState={() => (
                  <div className="p-8 text-center">
                    <h3 className="text-lg font-semibold text-fg-default mb-2">
                      No productions found
                    </h3>
                    <p className="text-sm text-fg-muted">
                      Adjust your search criteria or create a callsheet from
                      scratch.
                    </p>
                  </div>
                )}
                className={cn(
                  'divide-y divide-border-default',
                  getDimmedContainerClassName(
                    rd.isPlaceholderData(productionsData)
                  )
                )}
              >
                {rowLevelValidation
                  .validDataFromRows(data.items)
                  .map(production => {
                    const status = getStatusInfo(production.status_id);
                    const statusBadge = status
                      ? getStatusBadgeVariant(status.status_type)
                      : null;
                    const subtitle = getProductionSubtitle(production);
                    const color = production.color;

                    return (
                      <ListBoxItem
                        key={production.id}
                        id={production.id}
                        textValue={`${production.title || 'no name'}${subtitle ? `, ${subtitle}` : ', no code'}, ${formatDate(production)}, ${statusBadge?.label || 'No status'}`}
                        className="relative"
                      >
                        {/* Color bar - positioned absolutely on the left, matching ProductionList design */}
                        {color && (
                          <span
                            className="absolute left-2 inset-y-2 w-[2px]"
                            style={{ backgroundColor: color }}
                            aria-hidden="true"
                          />
                        )}
                        <ListBoxItemContent
                          icon={getProductionIcon(production)}
                          primary={
                            production.title || (
                              <span className="text-fg-subtle italic">
                                (no name)
                              </span>
                            )
                          }
                          secondary={
                            subtitle || (
                              <span className="text-fg-subtle italic">
                                (no code)
                              </span>
                            )
                          }
                          extra={
                            <div className="flex items-center gap-3 text-sm text-fg-muted">
                              <span>{formatDate(production)}</span>
                              {statusBadge && (
                                <Badge
                                  variant={statusBadge.variant}
                                  tone="subtle"
                                  size="sm"
                                >
                                  {statusBadge.label}
                                </Badge>
                              )}
                            </div>
                          }
                        />
                      </ListBoxItem>
                    );
                  })}
              </ListBox>
            ))}
        </ScrollShadowWrapper>
      </div>
    </>
  );
}
